import { OnInit, Component } from '@angular/core';
import { NestedTreeControl } from '@angular/cdk/tree';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { SelectionModel } from '@angular/cdk/collections';

import { Partition } from '@classes/simulation/partition';
import { SimulationService } from '@services/simulation.service';
import { PartitionsTreeBase } from './partitions-tree-base/partitions-tree-base';
import { PartitionType } from '@enums/partition-type.enum';

@Component({
    selector: 'app-partitions-tree',
    templateUrl: './partitions-tree.component.html',
    styleUrls: ['./partitions-tree.component.scss']
})
export class PartitionsTreeComponent extends PartitionsTreeBase implements OnInit {
    treeControl: NestedTreeControl<Partition>;
    dataSource: MatTreeNestedDataSource<Partition>;

    partitionTypes = PartitionType;

    /** The selection for checklist (true = multiple)*/
    checklistSelection = new SelectionModel<Partition>(true);
    selectedCategory: string;

    constructor(
        private simulationSrv: SimulationService
    ) {
        super();
    }

    ngOnInit() {
        this.setTreeNodes();

        if (!this.isMultiple && this.selectedNode) {
            this.expandToNode(this.selectedNode);
        }
    }

    isVisible = (node: Partition) =>
        node.partitionDetails
    &&  node.partitionDetails.partitionType !== PartitionType.funding
    &&  node.partitionDetails.partitionType !== PartitionType.invest
    &&  node.partitionDetails.partitionType !== PartitionType.simulationFile
    &&  node.partitionDetails.partitionType !== PartitionType.other;

    hasNestedChild = (_: number, nodeData: Partition) =>
        nodeData.partitions && nodeData.partitions.some(this.isVisible);

    onSelectPart(node: Partition): void {
        this.selectedNode = node;
        this.simulationSrv.setSelectedPartition(node);
        this.simulationSrv.selectedPartitionChanged.next(node);
    }

    // Select node and deselect parents and children
    onToggleSelection(selectedNode: Partition): void {
        // If user select a node and multi categories selection is not allowed
        if (!this.checklistSelection.isSelected(selectedNode) && !this.allowMultiCategories) {
            this.handleCategorySwitch(selectedNode);
        }

        if (!this.checklistSelection.isEmpty()) {
            this.deselectChildren(selectedNode);
            this.deselectParents(selectedNode);
        }

        this.checklistSelection.toggle(selectedNode);

        this.toggleSelection.emit([...this.checklistSelection.selected.map(s => s.partitionDetails.code)]);
    }

    isFirstLevelNode(node: Partition): boolean {
        return this.partitions.some(partition => partition.partitionDetails.code === node.partitionDetails.code);
    }

    private expandToNode(selectedNode: Partition): void {
        const parent = this.findParent(this.partitions, selectedNode);

        if (parent) {
            this.treeControl.expand(parent);
            this.expandToNode(parent);
        }
    }

    private findParent(partitions: Partition[], selPart: Partition): Partition {
        let partFound: Partition;

        partitions.map(p => {
            let tempFound: Partition;
            if (p.partitionDetails.code === selPart.parentCode) {
                tempFound = p;
            } else if (p.partitions) {
                tempFound = this.findParent(p.partitions, selPart);
            }

            if (tempFound) {
                partFound = tempFound;
            }
        });

        return partFound;
    }

    private handleCategorySwitch(node: Partition): void {
        if (node.partitionDetails.articleType !== this.selectedCategory) {
            this.selectedCategory = node.partitionDetails.articleType;
            this.checklistSelection.clear();
        }
    }

    private deselectChildren(node: Partition): void {
        this.treeControl
            .getDescendants(node)
            .map(child => {
                this.checklistSelection.deselect(child);
            });
    }

    private deselectParents(node: Partition): void {
        // map into all descendants of each first level partition.
        this.partitions.map(partition => {
            this.treeControl
                .getDescendants(partition)
                .map(child => {
                    // If a descendant is parent of a selected one => deselect it
                    const descendants = this.treeControl.getDescendants(child);
                    if (descendants.some(descendant => descendant === node)) {
                        this.checklistSelection.deselect(child);
                    }
                });
        });
    }

    private setTreeNodes(): void {
        this.treeControl = new NestedTreeControl<Partition>(this._getChildren);
        this.dataSource = new MatTreeNestedDataSource();
        this.dataSource.data = this.filterVisiblePartitions(this.partitions);
    }

    private _getChildren = (node: Partition) => node.partitions;

    private filterVisiblePartitions(partitions: Partition[]): Partition[] {
        const filteredPartitions = partitions.filter(p => p.isVisible !== false);
        filteredPartitions.map(p => {
            if (p.partitions) {
                p.partitions = this.filterVisiblePartitions(p.partitions);
            }
        });

        return filteredPartitions;
    }
}
